export function formatDateOnly(value) {
  if (!value) return 'N/A'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'N/A'
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export function formatDateTime(value) {
  if (!value) return 'TBD'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'TBD'
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

function floorDateToMinuteBucket(date, bucketMinutes = 5) {
  const safeBucket = bucketMinutes > 0 ? bucketMinutes : 5
  const rounded = new Date(date)
  rounded.setMinutes(Math.floor(rounded.getMinutes() / safeBucket) * safeBucket, 0, 0)
  return rounded
}

export function formatRoundedDateTime(value, bucketMinutes = 5) {
  if (!value) return 'TBD'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'TBD'

  const rounded = floorDateToMinuteBucket(date, bucketMinutes)

  return rounded.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

export function formatCompactDateTime(value) {
  if (!value) return 'TBD'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'TBD'
  return date.toLocaleString(undefined, {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

export function formatCompactRoundedDateTime(value, bucketMinutes = 5) {
  if (!value) return 'TBD'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'TBD'

  const rounded = floorDateToMinuteBucket(date, bucketMinutes)

  return rounded.toLocaleString(undefined, {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

export function formatPercent(value) {
  if (value === null || value === undefined || value === '') return 'N/A'
  const num = Number(value)
  if (!Number.isFinite(num)) return 'N/A'
  return `${num.toFixed(1)}%`
}

export function formatLine(value) {
  if (value === null || value === undefined || value === '') return 'N/A'
  const num = Number(value)
  if (Number.isFinite(num)) {
    if (num > 0) return `+${num}`
    return String(num)
  }
  return String(value)
}

export function formatCurrency(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return 'N/A'
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(num)
}

export function formatMarketLabel(value) {
  const normalized = String(value || '').toLowerCase()
  if (normalized === 'spread') return 'Spread / ATS'
  if (normalized === 'moneyline') return 'Moneyline'
  if (normalized === 'total') return 'Total'
  return value || 'Unspecified'
}

export function isWeeklyFootballLeague(league) {
  const normalized = String(league || '').toUpperCase()
  return normalized === 'NFL' || normalized === 'NCAAF'
}

export function formatTimeframeLabel(value, league = '') {
  const normalizedValue = String(value || '')

  if (isWeeklyFootballLeague(league)) {
    if (normalizedValue === '1d') return '1w'
    if (normalizedValue === '7d') return 'Last 2w'
  }

  return normalizedValue || 'Unspecified'
}

export function sortTimeframes(values = [], league = '') {
  const desiredOrder = isWeeklyFootballLeague(league)
    ? ['1d', '7d', '1m', '3m', 'Current Season']
    : ['Current Season', '3m', '1m', '7d', '1d']

  const orderMap = new Map(desiredOrder.map((item, index) => [item, index]))

  return values.slice().sort((a, b) => {
    const aRank = orderMap.has(a) ? orderMap.get(a) : 999
    const bRank = orderMap.has(b) ? orderMap.get(b) : 999
    if (aRank !== bRank) return aRank - bRank
    return String(a).localeCompare(String(b))
  })
}

export function oppositeSpread(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return null
  return num * -1
}

export function impliedProbabilityFromAmericanOdds(value) {
  const number = Number(value)
  if (!Number.isFinite(number) || number === 0) return null
  if (number < 0) return (Math.abs(number) / (Math.abs(number) + 100)) * 100
  return (100 / (number + 100)) * 100
}

export function americanOddsFromProbabilityPercent(percent) {
  const number = Number(percent)
  if (!Number.isFinite(number) || number <= 0 || number >= 100) return null
  if (number === 50) return 100
  const probability = number / 100
  if (probability > 0.5) return Math.round(-(probability / (1 - probability)) * 100)
  return Math.round(((1 - probability) / probability) * 100)
}

export function averageAmericanOddsPair(rows = [], firstField, secondField) {
  const valid = rows
    .map((row) => ({
      first: impliedProbabilityFromAmericanOdds(row?.[firstField]),
      second: impliedProbabilityFromAmericanOdds(row?.[secondField])
    }))
    .filter((row) => Number.isFinite(row.first) && Number.isFinite(row.second))

  if (valid.length === 0) return null

  return {
    first: americanOddsFromProbabilityPercent(valid.reduce((sum, row) => sum + row.first, 0) / valid.length),
    second: americanOddsFromProbabilityPercent(valid.reduce((sum, row) => sum + row.second, 0) / valid.length)
  }
}
